/**
 * EditorCaptureMode — Camera capture and click capture overlay modes.
 * Temporarily resumes GameScene so the world is visible and interactive.
 */
import Phaser from 'phaser';
import { CANVAS_W, CANVAS_H, EditorEvents } from './EditorTypes';
import type { EditorState } from './EditorTypes';
import { EventBus } from '@/events/EventBus';
import { TECH } from '@/ui/TechUI';
import { TILE_SIZE } from '@/config/GameConfig';

const CAM_PAN_SPEED = 5;
const TECH_ACCENT = TECH.ACCENT;

export class EditorCaptureMode {
  private scene: Phaser.Scene;
  private state: EditorState;
  private overlay: Phaser.GameObjects.Container | null = null;
  private savedCamPos = { x: 0, y: 0, zoom: 1 };
  private keyListener: ((e: KeyboardEvent) => void) | null = null;
  private clickListener: ((ptr: Phaser.Input.Pointer) => void) | null = null;
  private camDragActive = false;
  private camDownHandler: ((ptr: Phaser.Input.Pointer) => void) | null = null;
  private camUpHandler: ((ptr: Phaser.Input.Pointer) => void) | null = null;
  private camMoveHandler: ((ptr: Phaser.Input.Pointer) => void) | null = null;
  private camWheelHandler: ((p: any, gx: any, dx: number, dy: number) => void) | null = null;
  private camUpdateHandler: (() => void) | null = null;
  /** Full-screen blocker so hidden editor panels can't intercept clicks */
  private captureBlocker: Phaser.GameObjects.Rectangle | null = null;

  constructor(scene: Phaser.Scene, state: EditorState) {
    this.scene = scene;
    this.state = state;

    EventBus.on(EditorEvents.CAPTURE_START, this.onCaptureStart, this);
  }

  /** Tracks whether current click capture is in screen-space mode */
  private clickCaptureScreenSpace = false;

  private onCaptureStart(data: { type: 'camera' | 'click' | 'rect'; screenSpace?: boolean }): void {
    if (data.type === 'camera') this.startCameraCapture();
    else if (data.type === 'rect') this.startRectCapture(data.screenSpace ?? false);
    else this.startClickCapture(data.screenSpace ?? false);
  }

  // ── Camera Capture ──

  private startCameraCapture(): void {
    this.state.mode = 'captureCamera';
    const gs = this.state.hostScene;
    if (!gs) return;

    // Save camera state
    const cam = gs.cameras.main;
    this.savedCamPos = { x: cam.scrollX, y: cam.scrollY, zoom: cam.zoom };

    // Resume host scene for rendering, but keep input locked —
    // we use our own direct controls (no lerp, instant response)
    this.scene.scene.resume(this.state.hostSceneKey);
    // Block GameScene's updateDynamicCamera() so it doesn't fight our scrollX/Y
    this.scene.registry.set('tutorialCameraLock', true);
    // Kill any active camera tweens + disable roundPixels (causes drag resistance)
    (cam as any).panEffect?.reset();
    (cam as any).zoomEffect?.reset();
    cam.stopFollow();
    cam.roundPixels = false;

    (this.scene as any).hidePanels?.();

    // Full-screen invisible blocker at high depth — prevents hidden editor
    // panels (timeline cards, step panel buttons) from intercepting pointer events
    this.captureBlocker = this.scene.add.rectangle(
      CANVAS_W / 2, CANVAS_H / 2, CANVAS_W, CANVAS_H, 0x000000, 0,
    ).setDepth(50).setInteractive();

    // Instruction overlay
    this.showOverlay('Drag to pan — Scroll to zoom — WASD/Arrows\nENTER to capture — ESC to cancel');

    // Enter to confirm
    this.keyListener = (e: KeyboardEvent) => {
      if (e.key === 'Enter') { this.confirmCameraCapture(); e.preventDefault(); }
    };
    window.addEventListener('keydown', this.keyListener);

    // ── Left-click drag to pan (named handlers for proper cleanup) ──
    this.camDownHandler = () => { this.camDragActive = true; };
    this.camUpHandler = () => { this.camDragActive = false; };
    this.camMoveHandler = (ptr: Phaser.Input.Pointer) => {
      if (!this.camDragActive || !ptr.isDown) return;
      const dx = ptr.x - ptr.prevPosition.x;
      const dy = ptr.y - ptr.prevPosition.y;
      if (dx === 0 && dy === 0) return;
      cam.scrollX -= dx / cam.zoom;
      cam.scrollY -= dy / cam.zoom;
    };
    this.scene.input.on('pointerdown', this.camDownHandler);
    this.scene.input.on('pointerup', this.camUpHandler);
    this.scene.input.on('pointermove', this.camMoveHandler);

    // ── Scroll to zoom (centered on pointer) ──
    this.camWheelHandler = (_p: any, _gx: any, _dx: number, dy: number) => {
      const ptr = this.scene.input.activePointer;
      const halfW = cam.width / 2;
      const halfH = cam.height / 2;
      const oldZoom = cam.zoom;
      const newZoom = Phaser.Math.Clamp(oldZoom - dy * 0.002, 0.3, 5);
      // Keep world point under pointer fixed (Phaser zooms from viewport center)
      const worldX = cam.scrollX + halfW + (ptr.x - halfW) / oldZoom;
      const worldY = cam.scrollY + halfH + (ptr.y - halfH) / oldZoom;
      cam.zoom = newZoom;
      cam.scrollX = worldX - halfW - (ptr.x - halfW) / newZoom;
      cam.scrollY = worldY - halfH - (ptr.y - halfH) / newZoom;
    };
    this.scene.input.on('wheel', this.camWheelHandler);

    // ── WASD / Arrow keys ──
    const kb = this.scene.input.keyboard;
    if (kb) {
      this.camUpdateHandler = () => {
        if (this.state.mode !== 'captureCamera') return;
        if (kb.addKey('LEFT').isDown || kb.addKey('A').isDown)  cam.scrollX -= CAM_PAN_SPEED / cam.zoom;
        if (kb.addKey('RIGHT').isDown || kb.addKey('D').isDown) cam.scrollX += CAM_PAN_SPEED / cam.zoom;
        if (kb.addKey('UP').isDown || kb.addKey('W').isDown)    cam.scrollY -= CAM_PAN_SPEED / cam.zoom;
        if (kb.addKey('DOWN').isDown || kb.addKey('S').isDown)  cam.scrollY += CAM_PAN_SPEED / cam.zoom;
      };
      this.scene.events.on('update', this.camUpdateHandler);
    }
  }

  private confirmCameraCapture(): void {
    const gs = this.state.hostScene;
    if (!gs) return;
    const cam = gs.cameras.main;

    // Write coords to selected step — viewport center in world space
    // Phaser formula: worldX = scrollX + halfW + (screenX - halfW) / zoom
    // At viewport center (screenX = halfW): worldX = scrollX + halfW
    const idx = this.state.selectedStepIndex;
    const step = this.state.sequence.steps[idx];
    if (step && (step.type === 'cameraPan')) {
      step.target = {
        x: Math.round(cam.scrollX + cam.width / 2),
        y: Math.round(cam.scrollY + cam.height / 2),
      };
      step.zoom = Math.round(cam.zoom * 100) / 100;
      EventBus.emit(EditorEvents.STEP_UPDATED);
    }

    this.endCapture();
  }

  // ── Click Capture ──

  private startClickCapture(screenSpace = false): void {
    this.state.mode = 'captureClick';
    this.clickCaptureScreenSpace = screenSpace;
    const gs = this.state.hostScene;
    if (!gs) return;
    const cam = gs.cameras.main;

    // Save camera state
    this.savedCamPos = { x: cam.scrollX, y: cam.scrollY, zoom: cam.zoom };

    this.scene.scene.resume(this.state.hostSceneKey);
    this.scene.registry.set('tutorialCameraLock', true);
    (cam as any).panEffect?.reset();
    (cam as any).zoomEffect?.reset();
    cam.stopFollow();
    cam.roundPixels = false;

    (this.scene as any).hidePanels?.();
    const modeLabel = screenSpace ? 'SCREEN SPACE (UI)' : 'WORLD SPACE (Map)';
    const navHint = screenSpace
      ? `[${modeLabel}] Click to capture screen position\nENTER = center of screen — ESC to cancel`
      : `[${modeLabel}] Drag to pan — Scroll to zoom — Click to capture position\nENTER = center of screen — ESC to cancel`;
    this.showOverlay(navHint);

    // Full-screen blocker
    this.captureBlocker = this.scene.add.rectangle(
      CANVAS_W / 2, CANVAS_H / 2, CANVAS_W, CANVAS_H, 0x000000, 0,
    ).setDepth(50).setInteractive();

    // Crosshair that follows mouse
    const crosshair = this.scene.add.graphics().setDepth(101);
    const drawCross = (x: number, y: number) => {
      crosshair.clear();
      crosshair.lineStyle(1.5, 0xff8844, 0.8);
      crosshair.lineBetween(x - 12, y, x + 12, y);
      crosshair.lineBetween(x, y - 12, x, y + 12);
      crosshair.lineStyle(1, 0xff8844, 0.3);
      crosshair.strokeCircle(x, y, 12);
    };

    // Coord label that follows mouse
    const coordLabel = this.scene.add.text(0, 0, '', {
      fontSize: '10px', color: '#ff8844', fontFamily: 'monospace',
      backgroundColor: '#000000aa', padding: { x: 4, y: 2 },
    }).setDepth(101).setOrigin(0, 1);

    // Track drag vs click: only capture if pointer didn't drag
    let dragDist = 0;
    let lastX = 0, lastY = 0;

    // Drag to pan
    this.camDownHandler = (ptr: Phaser.Input.Pointer) => {
      this.camDragActive = true;
      dragDist = 0;
      lastX = ptr.x;
      lastY = ptr.y;
    };
    this.camUpHandler = (ptr: Phaser.Input.Pointer) => {
      if (this.camDragActive && dragDist < 5) {
        // This was a click, not a drag — capture position
        this.confirmClickCapture(ptr.x, ptr.y);
      }
      this.camDragActive = false;
    };
    this.camMoveHandler = (ptr: Phaser.Input.Pointer) => {
      // Update crosshair + coords
      drawCross(ptr.x, ptr.y);
      if (screenSpace) {
        coordLabel.setPosition(ptr.x + 16, ptr.y - 4).setText(`screen: (${Math.round(ptr.x)}, ${Math.round(ptr.y)})`);
      } else {
        const halfW = cam.width / 2;
        const halfH = cam.height / 2;
        const wx = Math.round(cam.scrollX + halfW + (ptr.x - halfW) / cam.zoom);
        const wy = Math.round(cam.scrollY + halfH + (ptr.y - halfH) / cam.zoom);
        coordLabel.setPosition(ptr.x + 16, ptr.y - 4).setText(`world: (${wx}, ${wy})`);
      }

      if (!this.camDragActive || !ptr.isDown) return;
      const dx = ptr.x - lastX;
      const dy = ptr.y - lastY;
      lastX = ptr.x;
      lastY = ptr.y;
      dragDist += Math.abs(dx) + Math.abs(dy);
      if (dx === 0 && dy === 0) return;
      if (!screenSpace) {
        cam.scrollX -= dx / cam.zoom;
        cam.scrollY -= dy / cam.zoom;
      }
    };

    // Scroll to zoom (world mode only)
    if (!screenSpace) {
      this.camWheelHandler = (_p: any, _gx: any, _dx: number, dy: number) => {
        const ptr = this.scene.input.activePointer;
        const halfW = cam.width / 2;
        const halfH = cam.height / 2;
        const oldZoom = cam.zoom;
        const newZoom = Phaser.Math.Clamp(oldZoom - dy * 0.002, 0.3, 5);
        const worldX = cam.scrollX + halfW + (ptr.x - halfW) / oldZoom;
        const worldY = cam.scrollY + halfH + (ptr.y - halfH) / oldZoom;
        cam.zoom = newZoom;
        cam.scrollX = worldX - halfW - (ptr.x - halfW) / newZoom;
        cam.scrollY = worldY - halfH - (ptr.y - halfH) / newZoom;
      };
    }

    // WASD / Arrow keys (world mode only)
    if (!screenSpace) {
      const kb = this.scene.input.keyboard;
      if (kb) {
        this.camUpdateHandler = () => {
          if (this.state.mode !== 'captureClick') return;
          let moved = false;
          if (kb.addKey('LEFT').isDown || kb.addKey('A').isDown)  { cam.scrollX -= CAM_PAN_SPEED / cam.zoom; moved = true; }
          if (kb.addKey('RIGHT').isDown || kb.addKey('D').isDown) { cam.scrollX += CAM_PAN_SPEED / cam.zoom; moved = true; }
          if (kb.addKey('UP').isDown || kb.addKey('W').isDown)    { cam.scrollY -= CAM_PAN_SPEED / cam.zoom; moved = true; }
          if (kb.addKey('DOWN').isDown || kb.addKey('S').isDown)  { cam.scrollY += CAM_PAN_SPEED / cam.zoom; moved = true; }
          if (moved) { /* keys pan only, no capture */ }
        };
        this.scene.events.on('update', this.camUpdateHandler);
      }
    }

    // ENTER = capture center of screen
    this.keyListener = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        this.confirmClickCapture(CANVAS_W / 2, CANVAS_H / 2);
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', this.keyListener);

    // Register input handlers after a short delay to avoid the button click triggering capture
    this.scene.time.delayedCall(100, () => {
      if (this.state.mode !== 'captureClick') return;
      if (this.camDownHandler) this.scene.input.on('pointerdown', this.camDownHandler);
      if (this.camUpHandler) this.scene.input.on('pointerup', this.camUpHandler);
      if (this.camMoveHandler) this.scene.input.on('pointermove', this.camMoveHandler);
      if (this.camWheelHandler) this.scene.input.on('wheel', this.camWheelHandler);
    });

    // Store crosshair/label for cleanup
    this._clickCaptureGfx = [crosshair, coordLabel];
  }

  private _clickCaptureGfx: Phaser.GameObjects.GameObject[] = [];

  /** Finalize click capture at given screen coordinates */
  private confirmClickCapture(screenX: number, screenY: number): void {
    const gs = this.state.hostScene;
    if (!gs) return;
    const cam = gs.cameras.main;
    const halfW = cam.width / 2;
    const halfH = cam.height / 2;

    // In screen-space mode, save screen coords directly; otherwise convert to world
    const targetX = this.clickCaptureScreenSpace
      ? Math.round(screenX)
      : Math.round(cam.scrollX + halfW + (screenX - halfW) / cam.zoom);
    const targetY = this.clickCaptureScreenSpace
      ? Math.round(screenY)
      : Math.round(cam.scrollY + halfH + (screenY - halfH) / cam.zoom);

    const idx = this.state.selectedStepIndex;
    const step = this.state.sequence.steps[idx];
    if (step && (step.type === 'fakeClick' || step.type === 'cameraPan')) {
      (step as any).target = { x: targetX, y: targetY };
      EventBus.emit(EditorEvents.STEP_UPDATED);
    }
    if (step && step.type === 'dialogue') {
      step.worldAnchor = { x: targetX, y: targetY };
      EventBus.emit(EditorEvents.STEP_UPDATED);
    }
    // Action steps (placeRobot): convert world coords to tile col/row
    if (step && step.type === 'action' && (step as any).action === 'placeRobot') {
      const col = Math.floor(targetX / TILE_SIZE);
      const row = Math.floor(targetY / TILE_SIZE);
      (step as any).params = { ...(step as any).params, col, row };
      EventBus.emit(EditorEvents.STEP_UPDATED);
    }

    this.endCapture();
  }

  // ── Rect Capture (for spotlight custom target) ──

  /**
   * Rect capture mode for spotlight targets.
   * @param screenSpace true = capture in screen pixels (UI elements), false = world coords (map)
   */
  private startRectCapture(screenSpace: boolean): void {
    this.state.mode = 'captureRect' as any;
    const gs = this.state.hostScene;
    if (!gs) return;
    const cam = gs.cameras.main;

    this.savedCamPos = { x: cam.scrollX, y: cam.scrollY, zoom: cam.zoom };
    this.scene.scene.resume(this.state.hostSceneKey);
    this.scene.registry.set('tutorialCameraLock', true);
    (cam as any).panEffect?.reset();
    (cam as any).zoomEffect?.reset();
    cam.stopFollow();
    cam.roundPixels = false;

    (this.scene as any).hidePanels?.();

    const modeLabel = screenSpace ? 'SCREEN SPACE (UI)' : 'WORLD SPACE (Map)';
    const navHint = screenSpace
      ? `[${modeLabel}] Right-click drag to draw rect\nENTER to confirm — ESC to cancel`
      : `[${modeLabel}] Right-click drag to draw rect — Left drag to pan — Scroll to zoom\nENTER to confirm — ESC to cancel`;
    this.showOverlay(navHint);

    this.captureBlocker = this.scene.add.rectangle(
      CANVAS_W / 2, CANVAS_H / 2, CANVAS_W, CANVAS_H, 0x000000, 0,
    ).setDepth(50).setInteractive();

    // Visual rect being drawn
    const rectGfx = this.scene.add.graphics().setDepth(101);
    const coordLabel = this.scene.add.text(0, 0, '', {
      fontSize: '10px', color: '#ffcc00', fontFamily: 'monospace',
      backgroundColor: '#000000aa', padding: { x: 4, y: 2 },
    }).setDepth(101).setOrigin(0, 1);

    // Show existing rect if any
    const idx = this.state.selectedStepIndex;
    const step = this.state.sequence.steps[idx];
    let currentRect: { x: number; y: number; w: number; h: number } | null = null;
    if (step && step.type === 'spotlight' && typeof (step as any).target === 'object' && !Array.isArray((step as any).target)) {
      currentRect = { ...(step as any).target };
    }

    // Coordinate conversion helpers (only used in world mode)
    const screenToWorld = (sx: number, sy: number) => {
      const halfW = cam.width / 2;
      const halfH = cam.height / 2;
      return {
        x: Math.round(cam.scrollX + halfW + (sx - halfW) / cam.zoom),
        y: Math.round(cam.scrollY + halfH + (sy - halfH) / cam.zoom),
      };
    };
    const worldToScreen = (wx: number, wy: number) => {
      const halfW = cam.width / 2;
      const halfH = cam.height / 2;
      return {
        x: (wx - cam.scrollX - halfW) * cam.zoom + halfW,
        y: (wy - cam.scrollY - halfH) * cam.zoom + halfH,
      };
    };

    // Convert drag start/end to target coords depending on mode
    const dragToRect = (sx1: number, sy1: number, sx2: number, sy2: number) => {
      if (screenSpace) {
        // Screen space: raw pixel coords
        const x = Math.round(Math.min(sx1, sx2));
        const y = Math.round(Math.min(sy1, sy2));
        return { x, y, w: Math.round(Math.abs(sx2 - sx1)), h: Math.round(Math.abs(sy2 - sy1)) };
      }
      // World space: convert from screen to world
      const a = screenToWorld(sx1, sy1);
      const b = screenToWorld(sx2, sy2);
      const x = Math.min(a.x, b.x);
      const y = Math.min(a.y, b.y);
      return { x, y, w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y) };
    };

    // Draw the rect on screen from stored target coords
    const drawCurrentRect = () => {
      rectGfx.clear();
      if (!currentRect) return;
      let sx: number, sy: number, sw: number, sh: number;
      if (screenSpace) {
        sx = currentRect.x; sy = currentRect.y;
        sw = currentRect.w; sh = currentRect.h;
      } else {
        const tl = worldToScreen(currentRect.x, currentRect.y);
        const br = worldToScreen(currentRect.x + currentRect.w, currentRect.y + currentRect.h);
        sx = tl.x; sy = tl.y; sw = br.x - tl.x; sh = br.y - tl.y;
      }
      rectGfx.fillStyle(TECH_ACCENT, 0.15);
      rectGfx.fillRect(sx, sy, sw, sh);
      rectGfx.lineStyle(2, TECH_ACCENT, 0.9);
      rectGfx.strokeRect(sx, sy, sw, sh);
      coordLabel.setPosition(sx, sy - 4)
        .setText(`(${currentRect.x}, ${currentRect.y}) ${currentRect.w}×${currentRect.h}`);
    };
    drawCurrentRect();

    // Right-click drag to draw rect, left-click drag to pan (world mode only)
    let drawing = false;
    let drawStartScreen = { x: 0, y: 0 };
    let lastX = 0, lastY = 0;

    this.camDownHandler = (ptr: Phaser.Input.Pointer) => {
      if (ptr.rightButtonDown()) {
        drawing = true;
        this.camDragActive = false;
        drawStartScreen = { x: ptr.x, y: ptr.y };
      } else if (!screenSpace) {
        // Left-click pan only in world mode
        this.camDragActive = true;
        drawing = false;
        lastX = ptr.x;
        lastY = ptr.y;
      }
    };

    this.camUpHandler = (ptr: Phaser.Input.Pointer) => {
      if (drawing) {
        drawing = false;
        const r = dragToRect(drawStartScreen.x, drawStartScreen.y, ptr.x, ptr.y);
        if (r.w > 5 && r.h > 5) {
          currentRect = r;
          drawCurrentRect();
        }
      }
      this.camDragActive = false;
    };

    this.camMoveHandler = (ptr: Phaser.Input.Pointer) => {
      if (drawing && ptr.isDown) {
        // Live rect preview while dragging
        const r = dragToRect(drawStartScreen.x, drawStartScreen.y, ptr.x, ptr.y);
        rectGfx.clear();
        // Always draw at screen pixel positions for live feedback
        const sx = screenSpace ? r.x : worldToScreen(r.x, r.y).x;
        const sy = screenSpace ? r.y : worldToScreen(r.x, r.y).y;
        const sw = screenSpace ? r.w : worldToScreen(r.x + r.w, r.y + r.h).x - sx;
        const sh = screenSpace ? r.h : worldToScreen(r.x + r.w, r.y + r.h).y - sy;
        rectGfx.fillStyle(TECH_ACCENT, 0.15);
        rectGfx.fillRect(sx, sy, sw, sh);
        rectGfx.lineStyle(2, TECH_ACCENT, 0.9);
        rectGfx.strokeRect(sx, sy, sw, sh);
        coordLabel.setPosition(sx, sy - 4)
          .setText(`(${r.x}, ${r.y}) ${r.w}×${r.h}`);
        return;
      }

      if (!screenSpace && this.camDragActive && ptr.isDown) {
        const dx = ptr.x - lastX;
        const dy = ptr.y - lastY;
        lastX = ptr.x;
        lastY = ptr.y;
        if (dx === 0 && dy === 0) return;
        cam.scrollX -= dx / cam.zoom;
        cam.scrollY -= dy / cam.zoom;
        drawCurrentRect();
      }
    };

    // Scroll to zoom (world mode only)
    if (!screenSpace) {
      this.camWheelHandler = (_p: any, _gx: any, _dx: number, dy: number) => {
        const ptr = this.scene.input.activePointer;
        const halfW = cam.width / 2;
        const halfH = cam.height / 2;
        const oldZoom = cam.zoom;
        const newZoom = Phaser.Math.Clamp(oldZoom - dy * 0.002, 0.3, 5);
        const worldX = cam.scrollX + halfW + (ptr.x - halfW) / oldZoom;
        const worldY = cam.scrollY + halfH + (ptr.y - halfH) / oldZoom;
        cam.zoom = newZoom;
        cam.scrollX = worldX - halfW - (ptr.x - halfW) / newZoom;
        cam.scrollY = worldY - halfH - (ptr.y - halfH) / newZoom;
        drawCurrentRect();
      };
    }

    // WASD / Arrow keys (world mode only)
    if (!screenSpace) {
      const kb = this.scene.input.keyboard;
      if (kb) {
        this.camUpdateHandler = () => {
          if ((this.state.mode as string) !== 'captureRect') return;
          let moved = false;
          if (kb.addKey('LEFT').isDown || kb.addKey('A').isDown) { cam.scrollX -= CAM_PAN_SPEED / cam.zoom; moved = true; }
          if (kb.addKey('RIGHT').isDown || kb.addKey('D').isDown) { cam.scrollX += CAM_PAN_SPEED / cam.zoom; moved = true; }
          if (kb.addKey('UP').isDown || kb.addKey('W').isDown) { cam.scrollY -= CAM_PAN_SPEED / cam.zoom; moved = true; }
          if (kb.addKey('DOWN').isDown || kb.addKey('S').isDown) { cam.scrollY += CAM_PAN_SPEED / cam.zoom; moved = true; }
          if (moved) drawCurrentRect();
        };
        this.scene.events.on('update', this.camUpdateHandler);
      }
    }

    // ENTER = confirm rect
    this.keyListener = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && currentRect) {
        this.confirmRectCapture(currentRect);
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', this.keyListener);

    // Disable context menu so right-click works for drawing
    const preventCtx = (e: Event) => e.preventDefault();
    this.scene.game.canvas.addEventListener('contextmenu', preventCtx);

    // Register input handlers after delay
    this.scene.time.delayedCall(100, () => {
      if ((this.state.mode as string) !== 'captureRect') return;
      if (this.camDownHandler) this.scene.input.on('pointerdown', this.camDownHandler);
      if (this.camUpHandler) this.scene.input.on('pointerup', this.camUpHandler);
      if (this.camMoveHandler) this.scene.input.on('pointermove', this.camMoveHandler);
      if (this.camWheelHandler) this.scene.input.on('wheel', this.camWheelHandler);
    });

    // Store graphics for cleanup + context menu restore
    this._clickCaptureGfx = [rectGfx, coordLabel];
    this._rectCtxMenuHandler = preventCtx;
  }

  private _rectCtxMenuHandler: ((e: Event) => void) | null = null;

  private confirmRectCapture(rect: { x: number; y: number; w: number; h: number }): void {
    const idx = this.state.selectedStepIndex;
    const step = this.state.sequence.steps[idx];
    if (step && step.type === 'spotlight') {
      (step as any).target = { x: rect.x, y: rect.y, w: rect.w, h: rect.h };
      EventBus.emit(EditorEvents.STEP_UPDATED);
    }
    this.endCapture();
  }

  // ── Common ──

  cancel(): void {
    // Restore camera if was capturing camera
    if (this.state.mode === 'captureCamera' && this.state.hostScene) {
      const cam = this.state.hostScene.cameras.main;
      cam.scrollX = this.savedCamPos.x;
      cam.scrollY = this.savedCamPos.y;
      cam.zoom = this.savedCamPos.zoom;
      cam.roundPixels = true;
    }
    this.endCapture();
  }

  private endCapture(): void {
    // Cleanup key listener
    if (this.keyListener) { window.removeEventListener('keydown', this.keyListener); this.keyListener = null; }
    this.clickListener = null;

    // Cleanup camera interaction handlers (all named — no leaks)
    if (this.camDownHandler) { this.scene.input.off('pointerdown', this.camDownHandler); this.camDownHandler = null; }
    if (this.camUpHandler) { this.scene.input.off('pointerup', this.camUpHandler); this.camUpHandler = null; }
    if (this.camMoveHandler) { this.scene.input.off('pointermove', this.camMoveHandler); this.camMoveHandler = null; }
    if (this.camWheelHandler) { this.scene.input.off('wheel', this.camWheelHandler); this.camWheelHandler = null; }
    if (this.camUpdateHandler) { this.scene.events.off('update', this.camUpdateHandler); this.camUpdateHandler = null; }
    this.camDragActive = false;

    // Remove click/rect capture graphics
    for (const gfx of this._clickCaptureGfx) gfx.destroy();
    this._clickCaptureGfx = [];

    // Restore context menu if rect capture disabled it
    if (this._rectCtxMenuHandler) {
      this.scene.game.canvas.removeEventListener('contextmenu', this._rectCtxMenuHandler);
      this._rectCtxMenuHandler = null;
    }

    // Remove blocker
    if (this.captureBlocker) { this.captureBlocker.destroy(); this.captureBlocker = null; }

    // Hide overlay
    this.overlay?.destroy();
    this.overlay = null;

    // Restore camera settings + release lock + pause host scene
    if (this.state.hostScene) {
      this.state.hostScene.cameras.main.roundPixels = true;
    }
    this.scene.registry.set('tutorialCameraLock', false);
    this.scene.scene.pause(this.state.hostSceneKey);
    (this.scene as any).showPanels?.();
    this.state.mode = 'edit';

    EventBus.emit(EditorEvents.CAPTURE_DONE);
  }

  private showOverlay(text: string): void {
    this.overlay?.destroy();
    this.overlay = this.scene.add.container(0, 0).setDepth(100);
    const bg = this.scene.add.rectangle(CANVAS_W / 2, 30, 500, 50, 0x000000, 0.7);
    const txt = this.scene.add.text(CANVAS_W / 2, 30, text, {
      fontSize: '13px', color: '#ffcc00', fontFamily: 'monospace', align: 'center',
    }).setOrigin(0.5);
    this.overlay.add([bg, txt]);
  }

  destroy(): void {
    EventBus.off(EditorEvents.CAPTURE_START, this.onCaptureStart, this);
    this.endCapture();
  }
}
